// Import Alpine.js
import Alpine from 'alpinejs';

// Import theme components
import contactForm from './components/contact-form';

// Make Alpine available globally
window.Alpine = Alpine;

// Register Alpine components
Alpine.data('contactForm', contactForm);

// ── Hero YouTube Background ──────────────────────────────────────────────────

/**
 * Load the YouTube IFrame API script once and resolve when ready.
 */
function loadYouTubeAPI() {
    return new Promise((resolve) => {
        if (window.YT && window.YT.Player) {
            resolve();
            return;
        }
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);
        window.onYouTubeIframeAPIReady = resolve;
    });
}

Alpine.data('heroVideo', (videoId) => ({
    videoReady: false,
    player: null,
    _loaded: false,

    async init() {
        // Skip entirely when no video ID, or on mobile (CSS already hides the container)
        if (!videoId || window.innerWidth < 1024) return;

        // Defer YouTube API load: wait 3s idle OR first scroll/interaction,
        // whichever comes first — avoids blocking TTI on page load.
        await new Promise((resolve) => {
            const load = () => {
                if (this._loaded) return;
                this._loaded = true;
                clearTimeout(timer);
                document.removeEventListener('scroll',     load, { passive: true });
                document.removeEventListener('mousemove',  load);
                document.removeEventListener('touchstart', load, { passive: true });
                resolve();
            };

            const timer = setTimeout(load, 3000);
            document.addEventListener('scroll',     load, { once: true, passive: true });
            document.addEventListener('mousemove',  load, { once: true });
            document.addEventListener('touchstart', load, { once: true, passive: true });
        });

        await loadYouTubeAPI();

        this.player = new YT.Player('hero-yt-player', {
            videoId,
            playerVars: {
                autoplay:       1,
                mute:           1,   // Required for autoplay in all browsers
                controls:       0,   // No playback controls
                showinfo:       0,
                modestbranding: 1,   // Minimal YouTube branding
                rel:            0,   // No related videos at end
                loop:           1,   // Loop playback
                playlist:       videoId, // Required for loop to work
                iv_load_policy: 3,   // No annotations
                disablekb:      1,   // No keyboard shortcuts
                fs:             0,   // No fullscreen button
                playsinline:    1,   // Inline on iOS
                origin:         window.location.origin,
            },
            events: {
                onReady: (event) => {
                    event.target.playVideo();
                },
                onStateChange: (event) => {
                    // Fade in on first play (state 1 = PLAYING)
                    if (event.data === 1 && !this.videoReady) {
                        setTimeout(() => { this.videoReady = true; }, 300);
                    }
                    // Belt-and-suspenders loop: restart on end (state 0 = ENDED)
                    if (event.data === 0) {
                        event.target.seekTo(0);
                        event.target.playVideo();
                    }
                },
            },
        });
    },
}));

// ── Scroll-reveal via IntersectionObserver ───────────────────────────────────
function initScrollReveal() {
    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible');
                    observer.unobserve(entry.target);
                }
            });
        },
        { threshold: 0.12 }
    );

    document.querySelectorAll('[data-reveal]').forEach((el) => {
        // Immediately reveal elements already visible in the viewport so they
        // are never hidden by the opacity:0 base style when JS is ready.
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
            el.classList.add('is-visible');
        } else {
            observer.observe(el);
        }
    });
}

// ── CTA scroll-to-contact ─────────────────────────────────────────────────────
function initCtaScroll() {
    document.querySelectorAll('a[href="#contact"]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
            const target = document.getElementById('contact');
            if (target) {
                e.preventDefault();
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        });
    });
}

// ── Gallery lightbox (product / case galleries) ───────────────────────────────
function initGallery() {
    Alpine.data('gallery', (images = []) => ({
        images,
        activeIndex: 0,
        open: false,

        openAt(index) {
            this.activeIndex = index;
            this.open = true;
            document.body.style.overflow = 'hidden';
        },

        close() {
            this.open = false;
            document.body.style.overflow = '';
        },

        prev() {
            this.activeIndex = (this.activeIndex - 1 + this.images.length) % this.images.length;
        },

        next() {
            this.activeIndex = (this.activeIndex + 1) % this.images.length;
        },
    }));
}

// ── Boot ──────────────────────────────────────────────────────────────────────
// Guard: DOMContentLoaded may already have fired when scripts are cached/deferred.
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        initScrollReveal();
        initCtaScroll();
    });
} else {
    initScrollReveal();
    initCtaScroll();
}

initGallery();

// Start Alpine
Alpine.start();
